// components/ErrorBoundary.tsx
'use client';

import React, { Component, ReactNode } from 'react';
import AppStorage from '@/utils/helpers';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  showErrorDetails?: boolean;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
}

export class ErrorBoundaryWrapper extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error details
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    this.setState({
      error,
      errorInfo,
    });

    // Optional: Send error to monitoring service
    // this.reportError(error, errorInfo);
  }

  private reportError = (error: Error, errorInfo: React.ErrorInfo) => {
    // Send to error reporting service like Sentry
    if (process.env.NODE_ENV === 'production') {
      // Example: Sentry.captureException(error, { extra: errorInfo });
      console.error('Production Error:', { error, errorInfo });
    }
  };

  private handleRefresh = () => {
    try {
      // Clear all storage
      AppStorage.clear();

      // Reset error boundary state
      this.setState({
        hasError: false,
        error: undefined,
        errorInfo: undefined,
      });

      // Redirect to home page
      window.location.href = '/';
    } catch (err) {
      // Fallback if storage clearing fails
      console.error('Error during refresh:', err);
      window.location.reload();
    }
  };

  private handleRetry = () => {
    // Just reset the error boundary without redirecting
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className="min-h-[400px] flex flex-col justify-center items-center bg-gray-50 rounded-lg border border-gray-200 p-8 m-4">
          <div className="text-center max-w-md">
            {/* Error Icon */}
            <div className="text-red-500 mb-6">
              <svg
                className="w-16 h-16 mx-auto"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>

            {/* Error Message */}
            <h3 className="text-xl font-semibold text-gray-900 mb-3">
              Something went wrong
            </h3>
            <p className="text-gray-600 mb-6">
              We encountered an unexpected error. Please try refreshing the
              page.
            </p>

            {/* Error Details (Development Only) */}
            {this.props.showErrorDetails &&
              process.env.NODE_ENV === 'development' &&
              this.state.error && (
                <details className="text-left mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <summary className="cursor-pointer font-medium text-red-800 mb-2">
                    Error Details (Development)
                  </summary>
                  <div className="text-sm">
                    <p className="font-medium text-red-700 mb-2">Message:</p>
                    <p className="text-red-600 mb-3 font-mono text-xs bg-white p-2 rounded border">
                      {this.state.error.message}
                    </p>
                    {this.state.error.stack && (
                      <>
                        <p className="font-medium text-red-700 mb-2">
                          Stack Trace:
                        </p>
                        <pre className="text-xs text-red-600 bg-white p-2 rounded border overflow-auto max-h-40 whitespace-pre-wrap">
                          {this.state.error.stack}
                        </pre>
                      </>
                    )}
                  </div>
                </details>
              )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={this.handleRetry}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Try Again
              </button>
              <button
                onClick={this.handleRefresh}
                className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition-colors font-medium focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
              >
                Refresh & Go Home
              </button>
            </div>

            <p className="text-xs text-gray-500 mt-4">
              If the problem persists, please contact support.
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
